import React, { FC, JSX, RefObject, useCallback, useEffect, useMemo } from "react";
import { LayoutChangeEvent, NativeScrollEvent, NativeSyntheticEvent, Platform, ScrollView } from "react-native";
import {
  View,
  useDevice,
  Spinner,
  Box,
  ALIGN,
  Banner,
  BANNER_TYPE,
  FLEX_DIRECTION,
  SPACE,
  Layout as AuroraLayout,
} from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Text } from "@lookiero/sty-psp-ui";
import { CompleteYourLookLook } from "../../../../../domain/projections/CompleteYourLookLook";
import { ProductPreview } from "../../../../../domain/projections/ProductPreview";
import { BackToTopButton } from "../../../components/backToTopButton/BackToTopButton";
import { CrossSellingBanner } from "../../../components/crossSellingBanner/CrossSellingBanner";
import { IconMessage } from "../../../components/iconMessage/IconMessage";
import { LookList } from "../../../components/lookList/LookList";
import { useCheckIfElementsAreInView } from "../../../hooks/useInView";
import { useStaticInfo } from "../../../hooks/useStaticInfo";
import { DOMAIN, I18nMessages } from "../../../i18n/i18n";
import { OS } from "../../../shared/config";
import { OfferingContent } from "../offeringLayout/OfferingContent";
import { style as completeYourLookStyle } from "./CompleteYourLook.style";

interface CompleteYourLookProps {
  readonly emptyState?: JSX.Element;
  readonly isLoading: boolean;
  readonly looks: CompleteYourLookLook[] | undefined;
  readonly scrollViewRef: RefObject<ScrollView | null>;
  readonly showHeader: boolean;
  readonly onCrossNavigation: (buttonId: string) => void;
  readonly onPressLook: ({ lookId, product }: { lookId: string; product: ProductPreview }) => void;
  readonly onScroll: (coords: { X: number; Y: number }) => void;
  readonly onLayoutLooks: (event: LayoutChangeEvent) => void;
  readonly onBack: () => void;
}

const CompleteYourLook: FC<CompleteYourLookProps> = ({
  emptyState,
  isLoading,
  looks,
  scrollViewRef,
  showHeader,
  onCrossNavigation,
  onPressLook,
  onScroll,
  onLayoutLooks,
  onBack,
}) => {
  const {
    cdnPath,
    session: {
      user: { segment },
    },
  } = useStaticInfo();
  const { screen } = useDevice();
  const isDesktop = screen.L;

  const style = useMemo(() => completeYourLookStyle(), []);

  const imageBannerUrl = useMemo(
    () => `${cdnPath}/images/shop/banners/complete-your-look/${segment}.webp`,
    [cdnPath, segment],
  );

  const cylTitleText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.CYL_TITLE });
  const cylSubtitleText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.CYL_SUBTITLE });
  const cylBottomTitleText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.CYL_BOTTOM_TITLE });
  const cylBottomSubtitleText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.CYL_BOTTOM_SUBTITLE });
  const cylCrossNavigationTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CYL_CROSS_NAVIGATION_TITLE,
  });
  const cylCrossNavigationDescriptionText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CYL_CROSS_NAVIGATION_DESCRIPTION,
  });
  const cylCrossNavigationButtonText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CYL_CROSS_NAVIGATION_CTA,
  });

  const hasLooks = Boolean(looks?.length);

  const checkIfElementsAreInView = useCheckIfElementsAreInView();
  const handleOnScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      onScroll({ X: event.nativeEvent.contentOffset.x, Y: event.nativeEvent.contentOffset.y });

      checkIfElementsAreInView();
    },
    [onScroll, checkIfElementsAreInView],
  );

  // Hack to fix that address bar issue in mobile browsers
  // https://developer.chrome.com/blog/url-bar-resizing/
  // https://css-tricks.com/the-trick-to-viewport-units-on-mobile/

  const onResize = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
    document.body.setAttribute("style", `height: calc(var(--vh, 1vh) * 100);`);
  };

  useEffect(() => {
    if (Platform.OS === OS.WEB) {
      window.addEventListener("resize", onResize);
    }
    return () => {
      if (Platform.OS === "web") {
        window.removeEventListener("resize", onResize);
      }
    };
  }, []);

  return (
    <OfferingContent
      scrollRef={scrollViewRef}
      style={{ scrollView: style.mainContentContainer, footer: style.footer }}
      footer={
        screen.S && looks?.length ? <BackToTopButton scrollViewRef={scrollViewRef} section="shop_cyl" /> : undefined
      }
      onScroll={handleOnScroll}
    >
      {isLoading ? (
        <View style={[style.container, style.centeredContainer]}>
          <Spinner />
        </View>
      ) : (
        <View style={style.container}>
          {looks?.length ? (
            <>
              {showHeader && (
                <View style={isDesktop ? style.titleWrapperDesktop : style.titleWrapperMobile}>
                  <Text
                    level={isDesktop ? 3 : 1}
                    style={isDesktop ? style.titleDesktop : style.titleMobile}
                    testID="home-title"
                    variant={isDesktop ? "heading" : "body"}
                  >
                    {cylTitleText}
                  </Text>

                  <Text
                    level={2}
                    style={[style.subtitle, isDesktop ? style.subtitleDesktop : undefined]}
                    variant={isDesktop ? "body" : "detail"}
                  >
                    {cylSubtitleText}
                  </Text>
                </View>
              )}

              <View style={isDesktop ? style.looksWrapperDesktop : style.looksWrapperMobile}>
                <LookList looks={looks} onLayoutLooks={onLayoutLooks} onPressLook={onPressLook} />
              </View>
            </>
          ) : (
            emptyState
          )}
          <AuroraLayout>
            <Box align={ALIGN.CENTER} paddingTop={!hasLooks ? SPACE.SPACE_16 : undefined} size={{ L: "2/3" }}>
              {hasLooks ? (
                <IconMessage body={cylBottomSubtitleText} style={style.bottomMessage} title={cylBottomTitleText} />
              ) : null}
              <View
                flexDirection={isDesktop ? FLEX_DIRECTION.ROW : FLEX_DIRECTION.COLUMN}
                marginBottom={isDesktop ? SPACE.SPACE_20 : SPACE.SPACE_8}
              >
                <Banner
                  description={cylCrossNavigationDescriptionText}
                  style={{ flex: 1 }}
                  title={cylCrossNavigationTitleText}
                  type={BANNER_TYPE.ROUNDED}
                  image={{
                    src: imageBannerUrl,
                  }}
                  primaryButton={{
                    text: cylCrossNavigationButtonText,
                    onPress: () => onCrossNavigation("cross-navigation-button"),
                  }}
                />
                <View margin={isDesktop ? SPACE.SPACE_3 : SPACE.SPACE_2} />
                <CrossSellingBanner style={style.crossSellingBanner} onPress={onBack} />
              </View>
            </Box>
          </AuroraLayout>
        </View>
      )}
    </OfferingContent>
  );
};

export type { CompleteYourLookProps };
export { CompleteYourLook };
